/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Dispatch, SetStateAction, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAuth } from "../../../context/Auth/AuthProvider";
import { COOKIE_NAMES, useMediaQueries } from "../../../shared";
import Button from "../../Button/Button";
import {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS
} from "../../Button/types";
import Dialog from "../../Dialog/Dialog";
import { CookieButtonType } from "../types";
import { CookiesMainDescription } from "./components/CookiesMainDescription";
import { CookiesModalContent } from "./components/CookiesModalContent";

interface CookiesModalProps {
  isOpen: boolean;
  onClose: Dispatch<SetStateAction<boolean>>;
  setShowBanner: Dispatch<SetStateAction<boolean>>;
}

const CookiesModal = ({
  isOpen,
  onClose,
  setShowBanner
}: CookiesModalProps) => {
  const { changeCookieConsentStatus } = useAuth();
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();

  const [isAnalyticsCookies, setIsAnalyticsCookies] = useState(false);

  const handleCloseDialog = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const handleOnlyEssentialCookies = () => {
    changeCookieConsentStatus(COOKIE_NAMES.All, false);
    handleCloseDialog();
    setShowBanner(false);
  };

  const handleAcceptSelected = () => {
    changeCookieConsentStatus(COOKIE_NAMES.All, isAnalyticsCookies);
    handleCloseDialog();
    setShowBanner(false);
  };

  const COOKIE_MODAL_ACTION_BUTTONS: CookieButtonType[] = [
    {
      color: BUTTON_COLORS.Primary,
      buttonText: t("Button##only essential"),
      action: handleOnlyEssentialCookies
    },
    {
      color: BUTTON_COLORS.Primary,
      buttonText: t("Button##accept selected"),
      action: handleAcceptSelected
    }
  ];

  return (
    <Dialog
      title={t("Cookies##cookie modal title")}
      description={<CookiesMainDescription />}
      open={isOpen}
      close={handleCloseDialog}
      isTitleSeparator
      isActionsSeparator
      slotProps={{
        backdrop: {
          invisible: true
        },
        transition: {
          onExited: () => setIsAnalyticsCookies(false)
        },
        root: {
          style: {
            zIndex: 1500
          }
        }
      }}
      actions={
        <div
          css={css({
            display: "flex",
            flexDirection: toMd ? "column-reverse" : "row",
            gap: "12px"
          })}
        >
          {COOKIE_MODAL_ACTION_BUTTONS.map((button: CookieButtonType) => (
            <Button
              key={button.buttonText}
              fullWidth
              color={button.color}
              size={BUTTON_SIZES.Normal}
              variant={BUTTON_VARIANTS.TextOnly}
              onClick={button.action}
            >
              {button.buttonText}
            </Button>
          ))}
        </div>
      }
    >
      <CookiesModalContent
        setIsAnalyticsCookies={setIsAnalyticsCookies}
        isAnalyticsCookies={isAnalyticsCookies}
        toMd={toMd}
      />
    </Dialog>
  );
};

export default CookiesModal;
